import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Cockpit from "@/pages/Cockpit";
import Dashboard from "@/pages/Dashboard";
import Portfolio from "@/pages/Portfolio";
import ClosedLoans from "@/pages/ClosedLoans";
import Pipeline from "@/pages/Pipeline";
import DSA from "@/pages/DSA";
import ApprovedLoans from "@/pages/ApprovedLoans";
import LoanDetail from "@/pages/LoanDetail";
import AppLayout from "@/components/AppLayout";
import StyleGuide from "@/pages/v3/StyleGuide";
import Foundation from "@/pages/v3/Foundation";
import Pipeline3 from "@/pages/v3/Pipeline3";
import Apply from "@/pages/v3/Apply";
import DsaConsole from "@/pages/v3/DsaConsole";
import LoanWorkspace from "@/pages/v3/LoanWorkspace";
import CoLending from "@/pages/v3/CoLending";
import BookV3 from "@/pages/v3/BookV3";
import Receivables from "@/pages/v3/Receivables";
import Recon from "@/pages/v3/Recon";
import Cfo from "@/pages/v3/Cfo";
import Regulatory from "@/pages/v3/Regulatory";
import Collections from "@/pages/v3/Collections";
import Truth from "@/pages/v3/Truth";
import Settings from "@/pages/v3/Settings";
import Portal from "@/pages/portal/Portal";
import PortalSignup from "@/pages/portal/PortalSignup";
import { NAV, canSee, firstAllowedRoute } from "@/lib/rbac";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0c0f1a] text-slate-300">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const ALL_NAV_ITEMS = NAV.flatMap((g) => g.items);

function RoleGate({ path, children }) {
  const { user } = useAuth();
  const role = user?.role || "admin";
  const item = ALL_NAV_ITEMS.find((it) => it.to === path);
  if (item && !canSee(role, item)) return <Navigate to={firstAllowedRoute(role)} replace />;
  return children;
}


function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/portal/signup" element={<PortalSignup />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<RoleGate path="/"><Cockpit /></RoleGate>} />
              <Route path="/portal" element={<RoleGate path="/portal"><Portal /></RoleGate>} />
              <Route path="/cfo" element={<RoleGate path="/cfo"><Cfo /></RoleGate>} />
              <Route path="/pipeline3" element={<RoleGate path="/pipeline3"><Pipeline3 /></RoleGate>} />
              <Route path="/apply" element={<RoleGate path="/apply"><Apply /></RoleGate>} />
              <Route path="/applyentry" element={<Navigate to="/apply" replace />} />
              <Route path="/dsa-console" element={<RoleGate path="/dsa-console"><DsaConsole /></RoleGate>} />
              <Route path="/book" element={<RoleGate path="/book"><BookV3 /></RoleGate>} />
              <Route path="/receivables" element={<RoleGate path="/receivables"><Receivables /></RoleGate>} />
              <Route path="/recon" element={<RoleGate path="/recon"><Recon /></RoleGate>} />
              <Route path="/collections3" element={<RoleGate path="/collections3"><Collections /></RoleGate>} />
              <Route path="/truth" element={<RoleGate path="/truth"><Truth /></RoleGate>} />
              <Route path="/colending" element={<RoleGate path="/colending"><CoLending /></RoleGate>} />
              <Route path="/regulatory" element={<RoleGate path="/regulatory"><Regulatory /></RoleGate>} />
              <Route path="/admin" element={<RoleGate path="/admin"><Settings /></RoleGate>} />
              <Route path="/foundation" element={<RoleGate path="/foundation"><Foundation /></RoleGate>} />
              <Route path="/loan/:id" element={<LoanWorkspace />} />
              <Route path="/styleguide" element={<StyleGuide />} />

              <Route path="/overview" element={<RoleGate path="/overview"><Dashboard /></RoleGate>} />
              <Route path="/portfolio" element={<RoleGate path="/portfolio"><Portfolio /></RoleGate>} />
              <Route path="/closed" element={<RoleGate path="/closed"><ClosedLoans /></RoleGate>} />
              <Route path="/closed-loans" element={<Navigate to="/closed" replace />} />
              <Route path="/portfolio/:id" element={<LoanDetail />} />
              <Route path="/pipeline" element={<RoleGate path="/pipeline"><Pipeline /></RoleGate>} />
              <Route path="/approved" element={<RoleGate path="/approved"><ApprovedLoans /></RoleGate>} />
              <Route path="/dsa" element={<RoleGate path="/dsa"><DSA /></RoleGate>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
